<script setup lang="ts">
    import type { AccessGroupNode } from '@/js/services/api/role/types';
    import { OrgLevelType } from '@/js/services/api/role/types';
    import EchoTooltip from '../EchoTooltip.vue';
    import { ChevronRightIcon, CirclePlusIcon, CircleMinusIcon } from '@lucide/vue';
    import { __ } from '@/js/stores/expressions';
    import { ref } from 'vue';

    const props = defineProps<{
        nodes: AccessGroupNode[];
        // Flat role-baseline lookup (keyed by node id) for the override badges.
        baselineById?: Map<string, AccessGroupNode>;
        depth?: number;
    }>();

    const currentDepth = props.depth ?? 0;

    const isGroupOrTeam = (node: AccessGroupNode) => node.orgLevelType !== OrgLevelType.Individual;

    const individuals = (node: AccessGroupNode) =>
        node.children.filter((c) => c.orgLevelType === OrgLevelType.Individual);

    // A team that grants all individual data shows its agents as a foldable list
    // rather than a parenthetical note. Teams that exclude individuals (or carry
    // no agents) keep the note instead.
    const isExpandable = (node: AccessGroupNode) =>
        node.orgLevelType === OrgLevelType.Team &&
        node.includeAllIndividuals &&
        individuals(node).length > 0;

    const expanded = ref<Record<string, boolean>>({});

    const toggleExpanded = (nodeId: string) => {
        expanded.value[nodeId] = !expanded.value[nodeId];
    };

    // A node belongs in the summary when its access is effectively granted, or
    // any of its (group/team) descendants is. Individuals are never listed on
    // their own — a team's annotation conveys whether individual data is included.
    const isVisible = (node: AccessGroupNode): boolean =>
        isGroupOrTeam(node) && (node.isSelected || node.children.some(isVisible));

    const visible = (nodes: AccessGroupNode[]) => nodes.filter(isVisible);

    // Override badges against the role baseline, mirroring OrgDataAccessTree's
    // diff: 'added' = the user grants a level the role lacks, 'removed' = the
    // user revokes a level the role grants (surfaced on the muted ancestor row
    // that the revocation leaves behind).
    const isAddedFromRole = (node: AccessGroupNode): boolean => {
        if (!props.baselineById) return false;
        const base = props.baselineById.get(node.id);
        return (
            (node.isSelected && !(base?.isSelected ?? false)) ||
            (node.includeAllIndividuals && !(base?.includeAllIndividuals ?? false)) ||
            (node.includeAllChildGroups && !(base?.includeAllChildGroups ?? false))
        );
    };

    const isRemovedFromRole = (node: AccessGroupNode): boolean => {
        if (!props.baselineById) return false;
        const base = props.baselineById.get(node.id);
        return (
            (!node.isSelected && (base?.isSelected ?? false)) ||
            (!node.includeAllIndividuals && (base?.includeAllIndividuals ?? false)) ||
            (!node.includeAllChildGroups && (base?.includeAllChildGroups ?? false))
        );
    };

    // The note only applies to teams whose agents aren't listed: an excluded
    // team, or an included team with no agents to fold out.
    const individualNote = (node: AccessGroupNode) =>
        node.orgLevelType === OrgLevelType.Team && !isExpandable(node)
            ? node.includeAllIndividuals
                ? __('user-summary-individual-included')
                : __('user-summary-individual-excluded')
            : '';
</script>

<template>
    <ul class="flex flex-col">
        <li v-for="node in visible(nodes)" :key="node.id">
            <div
                class="flex items-center gap-2 py-1 text-body"
                :style="{ paddingLeft: `${currentDepth * 1.25}rem` }"
            >
                <button
                    v-if="isExpandable(node)"
                    type="button"
                    class="shrink-0 ml-1.5 -mr-1.5 -my-1 rounded cursor-pointer custom-focus"
                    :aria-label="expanded[node.id] ? __('collapse') : __('expand')"
                    @click="toggleExpanded(node.id)"
                >
                    <ChevronRightIcon
                        class="size-6 transition-transform duration-150"
                        :class="{ '-rotate-90': expanded[node.id] }"
                    />
                </button>
                <span v-else class="w-6 shrink-0" />
                <!-- An unselected node only appears as the path to a granted
                     descendant; mute it so it doesn't read as itself granted. -->
                <span :class="{ 'line-through': !node.isSelected }">{{ node.name }}</span>
                <span v-if="individualNote(node)">({{ individualNote(node) }})</span>
                <EchoTooltip
                    v-if="isAddedFromRole(node)"
                    :label="__('user-org-access-added')"
                    class="inline-flex"
                    tabindex="0"
                >
                    <CirclePlusIcon class="size-4 text-primary-carrot" />
                </EchoTooltip>
                <EchoTooltip
                    v-else-if="isRemovedFromRole(node)"
                    :label="__('user-org-access-removed')"
                    class="inline-flex"
                    tabindex="0"
                >
                    <CircleMinusIcon class="size-4 text-primary-carrot" />
                </EchoTooltip>
            </div>
            <ul v-if="isExpandable(node) && expanded[node.id]" class="flex flex-col">
                <li
                    v-for="agent in individuals(node)"
                    :key="agent.id"
                    class="flex items-center gap-2 py-1 text-body text-gray"
                    :style="{ paddingLeft: `${(currentDepth + 1) * 1.25}rem` }"
                >
                    <span class="w-6 shrink-0" />
                    <span>{{ agent.name }}</span>
                </li>
            </ul>
            <SummaryOrgList
                v-if="visible(node.children).length"
                :nodes="node.children"
                :baseline-by-id="baselineById"
                :depth="currentDepth + 1"
            />
        </li>
    </ul>
</template>
